import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { House, Users, UserList, ChartBar, Gear, Receipt, SignOut, List, X, Package, ClipboardText, Warehouse, AddressBook, ShoppingCart } from "@phosphor-icons/react";
import { isAdmin, canAccessPath, ROLE_LABELS } from "@/lib/access";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: House },
  { path: "/my-dashboard", label: "My Dashboard", icon: House, salesOnly: true },
  { path: "/leads", label: "CRM Leads", icon: Users },
  { path: "/customers", label: "Customers", icon: AddressBook },
  { path: "/team", label: "Team Members", icon: UserList },
  { path: "/attendance", label: "Attendance", icon: ClipboardText },
  { path: "/purchase", label: "Purchase", icon: ShoppingCart },
  { path: "/inventory", label: "Inventory", icon: Warehouse },
  { path: "/product-rates", label: "Product Rates", icon: Package },
  { path: "/reports", label: "Reports", icon: ChartBar },
  { path: "/expenses", label: "Accounts", icon: Receipt },
  { path: "/settings", label: "Settings", icon: Gear },
];

const bottomNavItems = [
  { path: "/dashboard", label: "Home", icon: House },
  { path: "/leads", label: "Leads", icon: Users },
  { path: "/expenses", label: "Accounts", icon: Receipt },
  { path: "/product-rates", label: "Rates", icon: Package },
];

const salesBottomNavItems = [
  { path: "/my-dashboard", label: "Home", icon: House },
  { path: "/leads", label: "Leads", icon: Users },
  { path: "/customers", label: "Customers", icon: AddressBook },
  { path: "/product-rates", label: "Rates", icon: Package },
];

export default function Layout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const admin = isAdmin(user);
  const visibleNav = navItems.filter((i) => canAccessPath(user, i.path) && !(i.salesOnly && admin));
  const bottomItems = admin ? bottomNavItems : salesBottomNavItems;

  return (
    <div className="flex h-screen bg-gray-50" style={{ height: '100dvh' }} data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar — desktop + mobile drawer */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0f1117] text-white flex flex-col transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="sidebar">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Gupter', serif" }}>
            Huma <span className="text-indigo-400">Spices</span>
          </h1>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon size={18} weight={isActive ? "bold" : "regular"} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || "User"}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email || ""}</p>
              <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${admin ? "bg-indigo-500/20 text-indigo-300" : "bg-amber-500/20 text-amber-300"}`} data-testid="sidebar-role-badge">
                {ROLE_LABELS[user?.role] || "User"}
              </span>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="sidebar-logout"
          >
            <SignOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-3 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between lg:px-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 touch-target"
              data-testid="mobile-menu-btn"
            >
              <List size={22} />
            </button>
            <span className="text-sm font-bold text-gray-900 lg:hidden" style={{ fontFamily: "'Gupter', serif" }}>
              Huma <span className="text-indigo-600">Spices</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">Welcome,</span>
            <span className="text-xs sm:text-sm font-semibold text-gray-900">{user?.name || "User"}</span>
          </div>
        </header>

        {/* Page Content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 pb-20 sm:pb-4 lg:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-[10000] bg-white border-t border-gray-200 sm:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="mobile-bottom-nav">
        <div className="flex">
        {bottomItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 touch-target transition-colors ${isActive ? "text-indigo-600" : "text-gray-400"}`}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            >
              <Icon size={22} weight={isActive ? "bold" : "regular"} />
              <span className={`text-[10px] font-semibold ${isActive ? "text-indigo-600" : "text-gray-400"}`}>{item.label}</span>
              {isActive && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-indigo-600 rounded-full" />}
            </button>
          );
        })}
        {/* More menu to access Team, Reports, Settings */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 touch-target text-gray-400"
          data-testid="bottom-nav-more"
        >
          <List size={22} />
          <span className="text-[10px] font-semibold">More</span>
        </button>
        </div>
      </nav>
    </div>
  );
}
